import React, { useState, useEffect } from 'react';
import { CartIcon } from "../../appearance/icons/cart_icon";
import { cartManager } from './cartManager';
import { getAllPricesCount, findAllSelectedPricesValues } from './CartTotalPrice';
import { postFetchRequest } from "../../store/requests";

// Проверяем, находимся ли мы в кабинете пользователя
const isUserCabinet = () => {
    const hostname = window.location.hostname;
    const pathname = window.location.pathname;
    const hasToken = localStorage.getItem('token');
    
    // Отладочная информация
    console.log('Проверка контекста:', {
        hostname,
        pathname,
        hasToken: !!hasToken,
        cartServiceAvailable: !!window.cartService,
    });
    
    return (
        hostname.includes('cabinet') || 
        pathname.includes('/cabinet') ||
        (hasToken && window.cartService) // Есть токен И доступен cartService
    );
};

const AddToOrderButton = () => {
    const [canUserOpenOrder, setCanUserOpenOrder] = useState(false);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [totalCount, setTotalCount] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [responseMessage, setResponseMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [productTitle, setProductTitle] = useState('');
    const [distributors, setDistributors] = useState([]);
    const [userStatus, setUserStatus] = useState(null);
    // Убираем отдельный state для isUserLoaded, используем только userService.isLoaded
    
    // Динамически получаем cartService
    const getCartService = () => {
        return window.cartService || null;
    };

    // Динамически получаем userService
    const getUserService = () => {
        return window.userService || null;
    };
    
    // Проверяем, доступна ли корзина
    const isCartAvailable = () => {
        const inCabinet = isUserCabinet();
        const cartService = getCartService();
        console.log('isCartAvailable:', { 
            inCabinet, 
            cartService: !!cartService,
            result: inCabinet && cartService,
        });
        return inCabinet && cartService;
    };

    // Проверяем может ли пользователь добавлять товары в корзину
    const canUserAddToCart = () => {
        if (!isCartAvailable()) {
            return false;
        }

        const userService = getUserService();
        if (!userService) {
            console.log('🚫 [AddToOrderButton] No userService available');
            return false;
        }

        // ВАЖНО: Ждем загрузки информации о пользователе
        if (!userService.isLoaded) {
            console.log('⏳ [AddToOrderButton] User info not loaded yet');
            return false;
        }

        const canMakeOrders = userService.canMakeOrders();
        console.log('🛒 [AddToOrderButton] Can add to cart:', {
            isCartAvailable: isCartAvailable(),
            isLoaded: userService.isLoaded,
            canMakeOrders,
            userStatus,
            result: canMakeOrders,
        });

        return canMakeOrders;
    };
    
    // Отладочная функция
    const debugCartStatus = () => {
        console.log('=== DEBUG CART STATUS ===');
        console.log('URL:', window.location.href);
        console.log('Token:', !!localStorage.getItem('token'));
        console.log('window.cartService:', !!window.cartService);
        console.log('window.userService:', !!window.userService);
        console.log('isUserCabinet():', isUserCabinet());
        console.log('isCartAvailable():', isCartAvailable());
        console.log('User status:', userStatus);
        console.log('Is user loaded (from service):', getUserService()?.isLoaded);
        console.log('========================');
    };

    // Подписываемся на изменения информации о пользователе
    useEffect(() => {
        const userService = getUserService();
        if (userService) {
            const userStatusObserver = (user) => {
                console.log('👤 [AddToOrderButton] User status updated:', user);
                console.log('👤 [AddToOrderButton] UserService isLoaded:', userService.isLoaded);
                console.log('👤 [AddToOrderButton] Can make orders after update:', userService.canMakeOrders());
                setUserStatus(userService.getUserStatus());
                // Принудительно перерендериваем компонент когда статус пользователя изменяется
                setUserStatus(prevStatus => ({...userService.getUserStatus(), _timestamp: Date.now()}));
            };

            userService.subscribe(userStatusObserver);
            
            // Если информация уже загружена, обновляем статус
            if (userService.isLoaded) {
                console.log('👤 [AddToOrderButton] UserService already loaded, updating status');
                console.log('👤 [AddToOrderButton] Current user data:', userService.currentUser);
                console.log('👤 [AddToOrderButton] Can make orders:', userService.canMakeOrders());
                setUserStatus({...userService.getUserStatus(), _timestamp: Date.now()});
            } else {
                console.log('👤 [AddToOrderButton] UserService not loaded yet, waiting...');
            }

            return () => {
                userService.unsubscribe(userStatusObserver);
            };
        }
    }, []);

    const getProductTitle = () => {
        const productTitleElement = document.querySelector('.product-item__title');
        if (productTitleElement) {
            setProductTitle(productTitleElement.textContent);
        }
    };

    const getDistributors = () => {
        const distributorElements = document.querySelectorAll('.product-price__cart-distributor');
        const distributorNames = Array.from(distributorElements).map(element => element.textContent.trim());
        setDistributors(distributorNames);
    };

    useEffect(() => {
        getProductTitle();
        getDistributors();

        const observer = new MutationObserver(() => {
            getDistributors();
        });

        const cartContainer = document.querySelector('.product-price__cart');
        if (cartContainer) {
            observer.observe(cartContainer, {
                childList: true,
                subtree: true,
            });
        }

        cartManager.registerCartObserver(updateCartData);
        return () => {
            cartManager.unRegisterCartObserver(updateCartData);
            observer.disconnect();
        };
    }, []);

    const updateCartData = (pricesValue) => {
        setTotalCount(getAllPricesCount(pricesValue));
        setTotalPrice(findAllSelectedPricesValues(pricesValue));
        setCanUserOpenOrder(cartManager.isCountEnough() && getAllPricesCount(pricesValue) > 0);
    };

    const handleClick = async () => {
        // Отладочная информация
        debugCartStatus();
        
        setIsSubmitting(true);
        setResponseMessage('');
        setErrorMessage('');

        // Если пользователь может добавлять товары в корзину, добавляем товары в корзину
        if (canUserAddToCart()) {
            try {
                const cartService = getCartService();
                const currentPrices = cartManager.getCurrentPrices();
                
                const pricesArray = Object.values(currentPrices);
                
                // Получаем productId из URL страницы
                const productId = window.location.pathname.split('/').pop();
                
                for (const price of pricesArray) {
                    
                    if (price.count > 0) {
                        // Выбираем подходящую цену из quantities
                        let selectedQuantity = null;
                        if (price.quantities && price.quantities.length > 0) {
                            // Находим подходящую цену для указанного количества
                            selectedQuantity = price.quantities
                                .filter(q => q.minQty <= price.count)
                                .sort((a, b) => b.minQty - a.minQty)[0]; // Берем максимальную minQty <= count
                                
                            if (!selectedQuantity) {
                                selectedQuantity = price.quantities[0]; // Если не найдено, берем первую
                            }
                        }
                        
                        if (!selectedQuantity) {
                            console.error('❌ Не найдена цена для товара');
                            continue;
                        }
                        
                        const productData = {
                            productId: productId,
                            productCount: price.count,
                            productPriceId: selectedQuantity.id,
                        };
                        

                        
                        // Проверяем что все поля заполнены
                        if (!productId || !selectedQuantity.id || !price.count) {
                            console.error('❌ Некорректные данные для отправки:', {
                                productId: !!productId,
                                productPriceId: !!selectedQuantity.id,
                                productCount: !!price.count,
                            });
                            throw new Error('Отсутствуют обязательные поля для добавления товара');
                        }
                        
                        try {
                            await cartService.addProductToCart(productData);
                        } catch (addError) {
                            console.error('❌ Ошибка добавления товара:', addError);
                            throw addError; // Прерываем цикл если не удалось добавить товар
                        }
                    }
                }
                
                setResponseMessage('Товары добавлены в корзину!');
                cartManager.clearService(); // Очищаем локальную корзину
                
            } catch (error) {
                console.error('❌ Ошибка добавления в корзину:', error);
                setErrorMessage(`Ошибка добавления в корзину: ${error.message || 'Неизвестная ошибка'}`);
            }
        } else {
            // Старая логика для отправки email заявки
            if (!name || !phone) {
                setIsSubmitting(false);
                setErrorMessage('Имя и телефон обязательны');
                return;
            }

            const formData = {
                name,
                phone,
                totalCount,
                totalPrice: totalPrice.toFixed(2),
                productTitle,
                distributors: distributors.join(', '),
            };

            const url = "https://api.auroraelec.ru/api/v1/product/send-email/";

            postFetchRequest(url, formData, 
                (response) => {
                    setResponseMessage('Заявка успешно отправлена!');
                    setName('');
                    setPhone('');
                }, 
                (error) => {
                    setErrorMessage('Ошибка при отправке. Попробуйте снова.');
                },
            );
        }

        setIsSubmitting(false);
    };

    return (
        <>
            {(!localStorage.getItem('token') && !isCartAvailable()) ? (
                <button
                    className="button product-price__cart-button"
                    type="button"
                    onClick={() => {
                        window.location.href = '/register';
                    }}
                >
                    Зарегистрироваться
                </button>
            ) : (
                <button
                    className={`button product-price__cart-button ${(canUserOpenOrder && canUserAddToCart()) ? '' : 'button_disabled'}`}
                    type="button"
                    disabled={!canUserOpenOrder || !canUserAddToCart()}
                    onClick={() => {
                        // Если в кабинете и пользователь может добавлять товары - сразу добавляем
                        if (canUserAddToCart()) {
                            handleClick();
                        } else {
                            // Иначе открываем попап (для заявок)
                            setIsPopupOpen(true);
                        }
                    }}
                    title={
                        getUserService() && !getUserService().isLoaded && isCartAvailable() 
                            ? 'Загрузка информации о пользователе...' 
                            : !canUserAddToCart() && userStatus 
                                ? userStatus.message || 'Ваша учетная запись ожидает одобрения администратором' 
                                : ''
                    }
                >
                    {getUserService() && !getUserService().isLoaded && isCartAvailable() 
                        ? 'Загрузка...' 
                        : isCartAvailable() 
                            ? 'Добавить в корзину' 
                            : 'Заказать'
                    }
                    <CartIcon nameOfClass={`icon product-price__cart-icon ${(canUserOpenOrder && canUserAddToCart()) ? '' : 'product-price__cart-icon_disabled'}`} />
                </button>
            )}

            {(isPopupOpen || responseMessage || errorMessage) && (
                <div className="popup">
                    <div className="popup__content">
                        {(responseMessage || errorMessage) ? (
                            <div className="popup__message">
                                {responseMessage && <div style={{ color: 'green' }}>{responseMessage}</div>}
                                {errorMessage && <div style={{ color: 'red' }}>{errorMessage}</div>}
                                {responseMessage && isCartAvailable() && (
                                    <div style={{ marginTop: '10px' }}>
                                        <button 
                                            className="popup__submit" 
                                            onClick={() => window.location.href = '/cart'}
                                            style={{ marginRight: '10px' }}
                                        >
                                            Перейти в корзину
                                        </button>
                                        <button 
                                            className="popup__submit" 
                                            onClick={() => {
                                                setIsPopupOpen(false);
                                                setResponseMessage('');
                                                setErrorMessage('');
                                            }}
                                        >
                                            Продолжить покупки
                                        </button>
                                    </div>
                                )}
                                {errorMessage && (
                                    <div style={{ marginTop: '10px' }}>
                                        <button 
                                            className="popup__submit" 
                                            onClick={() => {
                                                setIsPopupOpen(false);
                                                setResponseMessage('');
                                                setErrorMessage('');
                                            }}
                                        >
                                            Закрыть
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="popup__form">
                                <h2 className="popup__form-title">
                                    {isCartAvailable() ? 'Добавить в корзину' : 'Оформить заявку'}
                                </h2>
                                
                                {/* Показываем поля для заявки только если НЕ в кабинете */}
                                {!isCartAvailable() && (
                                    <>
                                        <input
                                            className="popup__form-input"
                                            type="text"
                                            placeholder="Ваше имя"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            required
                                        />
                                        <input
                                            className="popup__form-input"
                                            type="tel"
                                            placeholder="Ваш телефон"
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            required
                                        />
                                    </>
                                )}
                                
                                <p><strong>Общее количество:</strong> {totalCount} шт.</p>
                                <p><strong>Итоговая цена:</strong> {totalPrice.toFixed(2)} {(typeof localStorage !== 'undefined' && localStorage.getItem('elsi_price_currency') === 'USD') ? '$' : '₽'}</p>
                                <p><strong>Продукт:</strong> {productTitle}</p>
                                <p><strong>Дистрибьюторы:</strong> {distributors.join(', ')}</p>
                                {errorMessage && <p className="popup__error">{errorMessage}</p>}
                                <button 
                                    type="button" 
                                    className="popup__submit" 
                                    onClick={handleClick} 
                                    disabled={isSubmitting || !canUserAddToCart()}
                                >
                                    {isSubmitting 
                                        ? 'Обработка...' 
                                        : !canUserAddToCart() && userStatus
                                            ? userStatus.message || 'Ожидает одобрения'
                                            : isCartAvailable() 
                                                ? 'Добавить в корзину' 
                                                : 'Отправить заявку'
                                    }
                                </button>
                            </div>
                        )}
                        <button className="popup__close" onClick={() => {
                            setIsPopupOpen(false);
                            setResponseMessage('');
                            setErrorMessage('');
                        }}>
                            Закрыть
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default AddToOrderButton;
